import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { QueryKeys } from "@/queries/utils";
import { BillingPlanTerm, CompanyJobOpening } from "@/types";
import { MilestoneCompletionFormSchema } from "./schema";
import { useMakeMilestonePayment } from "../milestone-company-pay/mutation";

export const useMarkMilestoneAsDelivered = (args: {
  jobOpeningId: string;
  candidateStatusId: string;
  onSuccess?: (data: BillingPlanTerm) => void;
}) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { makeMilestonePayment } = useMakeMilestonePayment();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (data: MilestoneCompletionFormSchema) => {
      const token = await getAccessTokenSilently();

      return request<BillingPlanTerm>({
        method: "POST",
        path: `/api/jobOpening/billing-plan-terms/${data.milestone}/submit`,
        body: JSON.stringify({
          completionDate: data.completionDate.toISOString(),
        }),
        token,
      });
    },
    onSuccess(data) {
      args.onSuccess?.(data);

      toast.success("Milestone marked as complete!");

      qc.setQueryData<CompanyJobOpening>(
        QueryKeys.companyOpportunity(args.jobOpeningId),
        (old) => {
          if (!old) return;

          const opening = { ...old };

          if (!opening.candidates) return opening;

          const candidateIndex = opening.candidates?.findIndex(
            ({ id }) => id === args.candidateStatusId,
          );
          const candidateStatus = opening.candidates[candidateIndex];
          if (!candidateStatus) {
            return;
          }

          const billing = candidateStatus.billing;
          if (!billing) return;

          const terms = [...billing.terms];
          const termIdx = terms.findIndex(({ id }) => id === data.id);

          if (termIdx === -1) {
            return;
          }

          terms[termIdx] = data;
          billing.terms = terms;
          candidateStatus.billing = billing;
          opening.candidates[candidateIndex] = candidateStatus;

          return opening;
        },
      );

      void makeMilestonePayment({ billingTermId: data.id });
    },
  });

  return {
    markMilestoneAsComplete: mutate,
    isMarkingMilestoneAsComplete: isPending,
    markMilestoneAsCompleteError: error,
  };
};
